// markerHelpers.js

import { offsetCenter } from './mapHelpers'
import {
  watchUserPosition,
  stopWatchingUserPosition,
  getCurrentPositionWithFallback,
} from '../uitls/helpers'

// Variable globale pour stocker l'ID du watcher
let userPositionWatcher = null

/**
 * Gestion des markers et de la position utilisateur
 * @param {Array} markersArray - tableau des markers
 * @param {Object} options - { preferCache: bool, onRefined: fn|null }
 */
export async function refreshUserPosition(markersArray, options = {}) {
  const { preferCache = false, onRefined = null } = options
  try {
    // Utiliser la fonction avec fallback intelligent (cache rapide si demandé)
    const pos = await getCurrentPositionWithFallback({
      preferCache,
      onProgressiveUpdate: onRefined
        ? refined => {
            const userMarker = {
              isUserPosition: true,
              position: { lat: refined.lat, lng: refined.lng },
              heading: refined.heading,
              speed: refined.speed,
              content: 'Votre position',
            }
            const idx = markersArray.findIndex(m => m.isUserPosition)
            if (idx !== -1) markersArray[idx] = userMarker
            else markersArray.push(userMarker)
            onRefined(refined)
          }
        : null,
    })
    if (!pos?.lat || !pos?.lng) return null

    const userMarker = {
      isUserPosition: true,
      position: { lat: pos.lat, lng: pos.lng },
      heading: pos.heading,
      speed: pos.speed,
      content: 'Votre position',
    }

    const idx = markersArray.findIndex(m => m.isUserPosition)
    if (idx !== -1) markersArray[idx] = userMarker
    else markersArray.push(userMarker)

    return userMarker.position
  } catch (error) {
    // Si même le fallback échoue, on ne peut rien faire
    console.warn(
      'Impossible de récupérer la position utilisateur (même avec fallback):',
      error instanceof Error ? error.message : String(error),
    )
    return null
  }
}

/**
 * Démarre le suivi en temps réel de la position utilisateur
 * @param {Array} markersArray - tableau des markers
 * @param {Function} onUpdate - callback optionnel pour les mises à jour
 */
export function startRealTimeUserTracking(markersArray, onUpdate = null) {
  // Arrêter le suivi précédent s'il existe
  stopRealTimeUserTracking()

  userPositionWatcher = watchUserPosition(
    positionData => {
      // Mettre à jour le marker utilisateur
      const userMarker = {
        isUserPosition: true,
        position: { lat: positionData.lat, lng: positionData.lng },
        heading: positionData.heading,
        speed: positionData.speed,
        content: 'Votre position',
      }

      const idx = markersArray.findIndex(m => m.isUserPosition)
      if (idx !== -1) markersArray[idx] = userMarker
      else markersArray.push(userMarker)

      // Appeler le callback de mise à jour si fourni
      if (onUpdate && typeof onUpdate === 'function') {
        onUpdate(userMarker)
      }
    },
    {
      enableHighAccuracy: true,
      timeout: 30000,
      maximumAge: 1000, // Accepter les positions de moins de 1 seconde
    },
  )

  return userPositionWatcher
}

/**
 * Arrête le suivi en temps réel de la position utilisateur
 */
export function stopRealTimeUserTracking() {
  if (userPositionWatcher) {
    stopWatchingUserPosition(userPositionWatcher)
    userPositionWatcher = null
  }
}

/**
 * Centre la carte sur l'utilisateur.
 * Si explicitPos est fourni, pan immédiatement sans déclencher de fetch GPS.
 */
export async function centerOnUser(map, markersArray, offsetY, explicitPos = null) {
  try {
    // Position explicite fournie (ex: depuis handlePermissionAllowed) → pan immédiat
    if (explicitPos?.lat && explicitPos?.lng) {
      const existingMarker = markersArray.find(m => m.isUserPosition)
      if (!existingMarker) {
        markersArray.push({
          isUserPosition: true,
          position: { lat: explicitPos.lat, lng: explicitPos.lng },
          heading: explicitPos.heading,
          speed: explicitPos.speed,
          content: 'Votre position',
        })
      }
      if (map) map.panTo(offsetCenter(explicitPos, offsetY, map))
      return
    }

    // Marker existant disponible → pan immédiat sans attendre un nouveau fetch
    const existingMarker = markersArray.find(m => m.isUserPosition)
    if (existingMarker?.position && map) {
      map.panTo(offsetCenter(existingMarker.position, offsetY, map))
    }

    // Rafraîchir en utilisant le cache en priorité, puis re-centrer si nécessaire
    const userPos = await refreshUserPosition(markersArray, { preferCache: true })
    if (!userPos || !map) return
    map.panTo(offsetCenter(userPos, offsetY, map))
  } catch (error) {
    // Erreur de centrage sur l'utilisateur - gérable localement
    console.warn(
      'Impossible de centrer sur la position utilisateur:',
      error instanceof Error ? error.message : String(error),
    )
  }
}

export function addStepMarker(marker, markersArray, routeId = null) {
  markersArray.push({
    position: { lat: marker.lat, lng: marker.lng },
    isUserPosition: false,
    routeId,
    options: { title: marker.label || 'Étape' },
  })
}
